import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';

import classNames from '../../../utils/classnames';
import { controlClassName, popoverPanelClassName } from '../../../components/form/control';
import { Checkbox } from '../../../components/checkbox/Checkbox';

export interface DocumentFilterOption {
  key: string;
  name: string;
}

export const DOCUMENT_FILTER_OPTIONS: DocumentFilterOption[] = [
  { key: 'has-cmr', name: 'Heeft CMR' },
  { key: 'has-delivery-note', name: 'Heeft leverbon' },
  { key: 'has-weigh-slip', name: 'Heeft weegbon' },
  { key: 'has-pallet-note', name: 'Heeft palletbon' },
  { key: 'has-annex-ii', name: 'Heeft Annex II' },
  { key: 'has-t1-document', name: 'Heeft T1-document' },
  { key: 'has-annex-vii', name: 'Heeft Annex VII' },
  { key: 'no-cmr', name: 'Heeft geen CMR' },
  { key: 'no-delivery-note', name: 'Heeft geen leverbon' },
  { key: 'no-weigh-slip', name: 'Heeft geen weegbon' },
  { key: 'no-pallet-note', name: 'Heeft geen palletbon' },
  { key: 'no-annex-ii', name: 'Heeft geen Annex II' },
  { key: 'no-t1-document', name: 'Heeft geen T1-document' },
  { key: 'no-annex-vii', name: 'Heeft geen Annex VII' },
];

export const DOCUMENT_FILTER_KEYS = new Set(DOCUMENT_FILTER_OPTIONS.map((o) => o.key));

const PRESENT_OPTIONS = DOCUMENT_FILTER_OPTIONS.filter((o) => o.key.startsWith('has-'));
const MISSING_OPTIONS = DOCUMENT_FILTER_OPTIONS.filter((o) => o.key.startsWith('no-'));

// Per document type only one side can apply ("heeft" xor "heeft geen"), so
// selecting one clears its opposite. Conditions across document types combine
// with AND (e.g. geen CMR + geen leverbon = beide ontbreken).
const OPPOSITE_KEYS: Record<string, string> = {
  'has-cmr': 'no-cmr',
  'no-cmr': 'has-cmr',
  'has-delivery-note': 'no-delivery-note',
  'no-delivery-note': 'has-delivery-note',
  'has-weigh-slip': 'no-weigh-slip',
  'no-weigh-slip': 'has-weigh-slip',
  'has-pallet-note': 'no-pallet-note',
  'no-pallet-note': 'has-pallet-note',
  'has-annex-ii': 'no-annex-ii',
  'no-annex-ii': 'has-annex-ii',
  'has-t1-document': 'no-t1-document',
  'no-t1-document': 'has-t1-document',
  'has-annex-vii': 'no-annex-vii',
  'no-annex-vii': 'has-annex-vii',
};

function getTriggerLabel(selectedKeys: string[]): string {
  if (selectedKeys.length === 0) return 'Alle';
  const names = selectedKeys.map((k) => DOCUMENT_FILTER_OPTIONS.find((o) => o.key === k)?.name ?? k);
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

interface DocumentFilterSelectProps {
  /** Selected condition keys; empty = no document filter. */
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
}

/** Multi-select document presence filter for the orders list. */
export const DocumentFilterSelect: React.FC<DocumentFilterSelectProps> = ({ selectedKeys, onChange }) => {
  const [open, setOpen] = useState(false);
  const isAll = selectedKeys.length === 0;
  const fullLabel = selectedKeys.map((k) => DOCUMENT_FILTER_OPTIONS.find((o) => o.key === k)?.name ?? k).join(', ');

  const toggle = (key: string, checked: boolean) => {
    if (checked) {
      const opposite = OPPOSITE_KEYS[key];
      onChange([...selectedKeys.filter((k) => k !== key && k !== opposite), key]);
    } else {
      onChange(selectedKeys.filter((k) => k !== key));
    }
  };

  const renderOption = (option: DocumentFilterOption) => {
    const isChecked = selectedKeys.includes(option.key);
    return (
      <label
        key={option.key}
        className={classNames(
          'flex h-9 w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-sm transition-colors hover:bg-surface-hover',
          isChecked ? 'font-medium text-fg' : 'text-fg-secondary',
        )}
      >
        <Checkbox isChecked={isChecked} onChange={(checked) => toggle(option.key, checked)} />
        <span className="flex-1 truncate">{option.name}</span>
      </label>
    );
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          title={isAll ? 'Alle' : fullLabel}
          className={classNames(
            controlClassName({ focusWithin: false }),
            'flex items-center justify-between gap-2 px-2.5 text-left',
            '[&>span:first-child]:truncate',
          )}
        >
          <span className={classNames(isAll && 'text-fg-muted')}>{getTriggerLabel(selectedKeys)}</span>
          <CaretDownIcon className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={classNames(popoverPanelClassName, 'p-1')}
          style={{ minWidth: 'var(--radix-popover-trigger-width)', width: 'var(--radix-popover-trigger-width)' }}
        >
          <button
            type="button"
            className={classNames(
              'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left text-sm transition-colors hover:bg-surface-hover',
              isAll ? 'font-semibold text-fg' : 'text-fg-secondary',
            )}
            onClick={() => {
              onChange([]);
              setOpen(false);
            }}
          >
            <span className="flex-1">Alle</span>
            {isAll && <CheckIcon className="h-4 w-4 shrink-0 text-brand-text" weight="bold" />}
          </button>

          <div className="mx-2 my-1 h-px bg-border" />
          <div className="px-2.5 pb-0.5 pt-1 text-xs font-medium text-fg-muted">Heeft</div>
          {PRESENT_OPTIONS.map(renderOption)}

          <div className="mx-2 my-1 h-px bg-border" />
          <div className="px-2.5 pb-0.5 pt-1 text-xs font-medium text-fg-muted">Heeft geen</div>
          {MISSING_OPTIONS.map(renderOption)}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
